// src/services/rideService.js
/**
 * ZUNO — rideService.js v3.4
 * v3.4:
 *  - geohashVizinhos agora usa grade 3x3 completa (9 células ~150km de raio)
 *    garantindo que corridas a 25km+ sempre apareçam para o motorista
 *  - aceitarCorrida: campos motoristaCor e motoristaTipoVeiculo alinhados
 *    com as regras do Firestore (estavam causando "Missing or insufficient permissions")
 */

import {
  collection, addDoc, updateDoc, doc, onSnapshot,
  query, where, orderBy, serverTimestamp, getDoc, getDocs, limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import { acceptDriverRideSecure } from "./driverOnboardingService";
import { advanceRideSecure, cancelRideSecure, changeRidePaymentMethodSecure, completeRideSecure, createRideSecure, submitRideRatingSecure } from "./zunoCardService";

// ─── CACHE LOCAL ────────────────────────────────────────────
let _ultimoHashPendentes = "";

function _hashCorridas(corridas) {
  return corridas.map(c => `${c.id}:${c.status}:${c.atualizadoEm?.seconds ?? 0}`).join("|");
}

// ─── GEOHASH SIMPLES (grade de ~50km) ───────────────────────
export function geohashSimples(lat, lng) {
  const gLat = Math.floor(lat / 0.45) * 0.45;
  const gLng = Math.floor(lng / 0.55) * 0.55;
  return `LAT${gLat.toFixed(2)}_LNG${gLng.toFixed(2)}`;
}

// ─── GRADE 3x3 COMPLETA (~150km de raio) ────────────────────
// v3.4: substituímos a cruz de 5 células pela grade 3x3 de 9 células.
//
// Por quê a cruz falhava?
//   Um motorista a 20km pode estar numa célula diagonal (NE, NO, SE, SO)
//   em relação à célula da corrida. A cruz não cobre diagonais, então a
//   corrida simplesmente não aparecia no listener do motorista.
//
// A grade 3x3 cobre todas as 8 direções + a própria célula, garantindo
// visibilidade de corridas em qualquer direção num raio de ~150km.
// Custo: 9 listeners vs 5 anteriores — ainda muito abaixo do limit(30)
// do fallback sem coordenadas.
export function geohashVizinhos(lat, lng) {
  const celulas = [];
  for (let dLat = -1; dLat <= 1; dLat++) {
    for (let dLng = -1; dLng <= 1; dLng++) {
      celulas.push(geohashSimples(lat + dLat * 0.45, lng + dLng * 0.55));
    }
  }
  return [...new Set(celulas)]; // dedup caso bordas coincidam
}

// ─── CRIAR CORRIDA (imediata OU agendada) ───────────────────
export async function criarCorrida({ origem, destino, categoria, pagamento, agendamento }) {
  const requestId = window.crypto?.randomUUID?.().replace(/-/g, "_")
    || `ride_${Date.now()}_${Math.random().toString(36).slice(2, 18)}`;
  const scheduledAt = agendamento?.data && agendamento?.hora
    ? new Date(`${agendamento.data}T${agendamento.hora}:00`).toISOString()
    : null;
  const result = await createRideSecure({
    requestId,
    origin: { lat: origem.lat, lng: origem.lng, address: origem.endereco || "" },
    destination: { lat: destino.lat, lng: destino.lng, name: destino.nome || "Destino", address: destino.endereco || "" },
    category: categoria,
    payment: {
      method: pagamento?.metodo || "dinheiro",
      cardId: pagamento?.metodo === "cartao_zuno" ? pagamento?.cardId || null : null,
      needsChange: pagamento?.precisaTroco === true,
      changeForCents: pagamento?.metodo === "dinheiro" && pagamento?.precisaTroco === true
        ? Math.round(Number(pagamento?.valorTroco) * 100)
        : null,
    },
    scheduledAt,
  });
  return result.rideId;
}

// eslint-disable-next-line no-unused-vars
async function criarCorridaLegacyDisabled({
  passageiroId, passageiroNome, passageiroFoto,
  origem, destino, categoria, dadosCorrida,
  pagamento,
  agendamento,
}) {
  let passageiroAvaliacao = null;
  let passageiroVerificado = false;
  try {
    const userSnap = await getDoc(doc(db, "users", passageiroId));
    if (userSnap.exists()) {
      const ud = userSnap.data();
      passageiroAvaliacao = ud.avaliacao || null;
      const temFoto = !!(ud.photoURL || passageiroFoto);
      const temTel = !!(ud.telefone && String(ud.telefone).replace(/\D/g,"").length >= 10);
      passageiroVerificado = temFoto && temTel;
    }
  } catch (e) { /* não bloqueia criação da corrida */ }

  const agendado = !!(agendamento?.data && agendamento?.hora);
  const agendadoPara = agendado
    ? new Date(`${agendamento.data}T${agendamento.hora}:00`).toISOString()
    : null;

  const dadosBase = {
    passageiroId,
    passageiroNome,
    passageiroFoto: passageiroFoto || "",
    passageiroVerificado,
    origem: {
      lat: origem.lat, lng: origem.lng,
      endereco: origem.endereco || "Minha localização",
    },
    destino: {
      lat: destino.lat, lng: destino.lng,
      nome: destino.nome || "Destino",
      endereco: destino.endereco || "",
    },
    categoria,
    valor: dadosCorrida.finalPrice,
    valorOriginal: dadosCorrida.precoOriginal || dadosCorrida.finalPrice,
    desconto: dadosCorrida.desconto || 0,
    temDesconto: dadosCorrida.temDesconto || false,
    valorMotorista: dadosCorrida.finalPrice,
    surgeFactor: dadosCorrida.surgeFactor || 1,
    isSurge: dadosCorrida.isSurge || false,
    surgeLabel: dadosCorrida.surgeLabel || "Normal",
    distanciaKm: dadosCorrida.distanciaKm || 0,
    tempoMin: dadosCorrida.tempoMin || 0,

    metodoPagamento: pagamento?.metodo || "dinheiro",
    paymentCardId: pagamento?.metodo === "cartao_zuno" ? pagamento?.cardId || null : null,
    paymentStatus: pagamento?.metodo === "cartao_zuno" ? "awaiting" : "external",
    precisaTroco: pagamento?.precisaTroco || false,
    trocoValor: pagamento?.trocoValor || null,        // valor do troco a devolver
    valorPagarDinheiro: pagamento?.valorTroco || null, // quanto o passageiro vai entregar
    taxaCartao: pagamento?.taxaCartao || 0,
    valorFinalComTaxa: pagamento?.valorFinal || dadosCorrida.finalPrice,
    trocoSolicitado: null,

    agendado,
    agendadoPara,
    agendamentoData: agendamento?.data || null,
    agendamentoHora: agendamento?.hora || null,

    regiao: geohashSimples(origem.lat, origem.lng),

    status: agendado ? "scheduled" : "pending",
    motoristaid: null,
    motoristaNome: null,
    motoristaFoto: null,
    motoristaMarca: null,
    motoristaModelo: null,
    motoristaPlaca: null,
    motoristaCor: null,
    motoristaTipoVeiculo: null,
    motoristaAvaliacao: null,
    pixMotorista: null,
    qrCodePix: null,
    temMaquininha: false,

    passageiroAvaliacao,
    avaliacaoPassageiro: null,
    avaliacaoMotorista: null,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  };

  if (agendado) {
    const rideRef = await addDoc(collection(db, "rides"), dadosBase);
    await addDoc(collection(db, "scheduled_rides"), {
      ...dadosBase,
      rideId: rideRef.id,
    });
    return rideRef.id;
  } else {
    const rideRef = await addDoc(collection(db, "rides"), dadosBase);
    return rideRef.id;
  }
}

// ─── CRIAR CORRIDA AGENDADA (standalone) ────────────────────
// eslint-disable-next-line no-unused-vars
async function criarCorridaAgendadaLegacyDisabled({
  passageiroId, passageiroNome, passageiroFoto,
  origem, destino, categoria, dadosCorrida,
  pagamento, agendamento,
}) {
  if (!agendamento?.data || !agendamento?.hora) {
    throw new Error("Agendamento precisa de data e hora.");
  }
  const agendadoPara = new Date(`${agendamento.data}T${agendamento.hora}:00`);
  if (agendadoPara <= new Date()) {
    throw new Error("O horário agendado deve ser no futuro.");
  }

  const dados = {
    passageiroId, passageiroNome,
    passageiroFoto: passageiroFoto || "",
    origem, destino, categoria,
    valor: dadosCorrida.finalPrice,
    valorMotorista: dadosCorrida.finalPrice,
    valorFinalComTaxa: pagamento?.valorFinal || dadosCorrida.finalPrice,
    distanciaKm: dadosCorrida.distanciaKm || 0,
    tempoMin: dadosCorrida.tempoMin || 0,
    metodoPagamento: pagamento?.metodo || "dinheiro",
    paymentCardId: pagamento?.metodo === "cartao_zuno" ? pagamento?.cardId || null : null,
    paymentStatus: pagamento?.metodo === "cartao_zuno" ? "awaiting" : "external",
    agendado: true,
    agendadoPara: agendadoPara.toISOString(),
    agendamentoData: agendamento.data,
    agendamentoHora: agendamento.hora,
    status: "scheduled",
    ativado: false,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "scheduled_rides"), dados);
  return ref.id;
}

// ─── ATIVAR CORRIDA AGENDADA ────────────────────────────────
// eslint-disable-next-line no-unused-vars
async function ativarCorridaAgendadaLegacyDisabled(scheduledId) {
  const ref = doc(db, "scheduled_rides", scheduledId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Corrida agendada não encontrada");

  const data = snap.data();
  if (data.ativado) return data.rideId;

  // Verifica se o passageiro já tem corrida ativa (evita 2 corridas simultâneas)
  const corridaAtivaSnap = await getDocs(query(
    collection(db, "rides"),
    where("passageiroId", "==", data.passageiroId),
    where("status", "in", ["pending", "accepted", "arrived", "in_progress", "completion_pending"]),
    limit(1)
  ));
  if (!corridaAtivaSnap.empty) {
    throw new Error("Passageiro já possui uma corrida em andamento");
  }

  const rideRef = await addDoc(collection(db, "rides"), {
    ...data,
    status: "pending",
    agendado: true,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  await updateDoc(ref, {
    status: "pending",
    ativado: true,
    lembreteEnviado: false,
    rideId: rideRef.id,
    ativadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  return rideRef.id;
}

// ─── ACEITAR CORRIDA ────────────────────────────────────────
// v3.4 CORREÇÃO: motoristaCor e motoristaTipoVeiculo adicionados à
// lista de campos permitidos nas regras do Firestore (eram a causa do
// "Missing or insufficient permissions" ao aceitar corrida)
export async function aceitarCorrida(rideId, motoristaId) {
  if (!motoristaId) throw new Error("Motorista não autenticado");
  return acceptDriverRideSecure(rideId);
}

// ─── RECUSAR CORRIDA ────────────────────────────────────────
export async function recusarCorrida(rideId) {
  // Recusar é uma ação local: a corrida continua disponível para outros
  // motoristas e não precisa de nenhuma gravação cliente no Firestore.
  return { dismissed: true, rideId };
}

// ─── MOTORISTA CHEGOU AO PASSAGEIRO ─────────────────────────
export async function chegouNoPassageiro(rideId) {
  return advanceRideSecure(rideId, "arrived");
}

// ─── INICIAR CORRIDA ────────────────────────────────────────
export async function iniciarCorrida(rideId) {
  return advanceRideSecure(rideId, "in_progress");
}

// ─── CONCLUIR CORRIDA ───────────────────────────────────────
export async function concluirCorrida(rideId) {
  return completeRideSecure(rideId);
}

// ─── CANCELAR CORRIDA ───────────────────────────────────────
export async function cancelarCorrida(rideId, canceladoPor = "passageiro") {
  void canceladoPor;
  return cancelRideSecure(rideId);
}

// ─── CANCELAR CORRIDA AGENDADA ──────────────────────────────
export async function cancelarCorridaAgendada(scheduledId) {
  return cancelRideSecure(scheduledId);
}

// ─── PASSAGEIRO CONFIRMOU PIX PAGO ──────────────────────────
export async function confirmarPixPago(rideId) {
  await updateDoc(doc(db, "rides", rideId), {
    pixPago: true,
    pixPagoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

// ─── BUSCAR CORRIDA POR ID ───────────────────────────────────
export async function getRideById(rideId) {
  try {
    const snap = await getDoc(doc(db, "rides", rideId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (error) {
    console.error("Erro ao buscar corrida:", error);
    return null;
  }
}

// ─── ATUALIZAR MÉTODO DE PAGAMENTO ───────────────────────────
export async function atualizarMetodoPagamento(rideId, metodo, dadosPagamento = {}) {
  return changeRidePaymentMethodSecure(rideId, metodo, dadosPagamento);
}

// ─── SOLICITAR TROCO ────────────────────────────────────────
export async function solicitarTroco(rideId, valor, mensagem = "") {
  await updateDoc(doc(db, "rides", rideId), {
    trocoSolicitado: { valor, mensagem, solicitadoEm: serverTimestamp(), confirmado: false },
    atualizadoEm: serverTimestamp(),
  });
}

// ─── CONFIRMAR TROCO ────────────────────────────────────────
export async function confirmarTroco(rideId) {
  await updateDoc(doc(db, "rides", rideId), {
    "trocoSolicitado.confirmado": true,
    "trocoSolicitado.confirmadoEm": serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
}

// ─── AVALIAR MOTORISTA ───────────────────────────────────────
export async function avaliarMotorista({ rideId, motoristaId, estrelas, elogios, comentario }) {
  void motoristaId;
  return submitRideRatingSecure(rideId, "driver", { estrelas, elogios, comentario });
}

// ─── AVALIAR PASSAGEIRO ─────────────────────────────────────
export async function avaliarPassageiro({ rideId, passageiroId, estrelas, elogios, comentario }) {
  void passageiroId;
  return submitRideRatingSecure(rideId, "passenger", { estrelas, elogios, comentario });
}

// ─── REPORTAR USUÁRIO ───────────────────────────────────────
export async function reportarUsuario({ rideId, reportadoPor, reportadoTipo, motivo, descricao }) {
  await addDoc(collection(db, "reports"), {
    rideId,
    reportadoPor,
    reportadoTipo,
    motivo,
    descricao: descricao || "",
    status: "pending",
    criadoEm: serverTimestamp(),
  });
}

// ─── CANCELAR CORRIDA COM MOTIVO ────────────────────────────
export async function cancelarCorridaComMotivo(rideId, motivo = "") {
  return cancelRideSecure(rideId, motivo);
}

// ─── HISTÓRICO DE CORRIDAS (com cursor pagination) ──────────
// cursorDoc: último documento retornado na página anterior (para paginar)
// pageSize:  quantos registros retornar por página (padrão 30)
// Retorna: { corridas: [...], proximoCursor: docSnapshot | null }
export async function getHistoricoCorridas(userId, userType = "passenger", { cursorDoc = null, pageSize = 30 } = {}) {
  try {
    const field = userType === "passenger" ? "passageiroId" : "motoristaid";
    const constraints = [
      where(field, "==", userId),
      where("status", "in", ["completed", "cancelled"]),
      orderBy("criadoEm", "desc"),
      limit(pageSize),
    ];
    if (cursorDoc) constraints.push(startAfter(cursorDoc));
    const q = query(collection(db, "rides"), ...constraints);
    const snap = await getDocs(q);
    const corridas = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const proximoCursor = snap.docs.length === pageSize ? snap.docs[snap.docs.length - 1] : null;
    return { corridas, proximoCursor };
  } catch (error) {
    console.error("Erro ao buscar histórico:", error);
    return { corridas: [], proximoCursor: null, error };
  }
}

// ─── CORRIDA ATIVA ───────────────────────────────────────────
export async function getCorridaAtiva(userId, userType = "passenger") {
  try {
    const field = userType === "passenger" ? "passageiroId" : "motoristaid";
    const q = query(
      collection(db, "rides"),
      where(field, "==", userId),
      where("status", "in", ["pending", "accepted", "arrived", "in_progress", "completion_pending"]),
      orderBy("criadoEm", "desc")
    );
    const snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
    return null;
  } catch (error) {
    console.error("Erro ao buscar corrida ativa:", error);
    return null;
  }
}

// ─── ESCUTAR UMA CORRIDA ESPECÍFICA ─────────────────────────
export function escutarCorrida(rideId, callback) {
  return onSnapshot(doc(db, "rides", rideId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

// ─── ESCUTAR CORRIDAS PENDENTES (motorista) ─────────────────
// v3.4: grade 3x3 completa (9 células) em vez da cruz de 5.
// Garante visibilidade de corridas em qualquer direção a ~25-150km.
export function escutarCorridasPendentes(callback, driverLat, driverLng) {
  _ultimoHashPendentes = "";
  let unsubs = [];
  let debounceTimer = null;
  const todasCorridas = new Map();

  const notificar = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const corridas = Array.from(todasCorridas.values())
        .sort((a, b) => (b.criadoEm?.seconds ?? 0) - (a.criadoEm?.seconds ?? 0));
      const novoHash = _hashCorridas(corridas);
      if (novoHash === _ultimoHashPendentes) return;
      _ultimoHashPendentes = novoHash;
      callback(corridas);
    }, 300);
  };

  if (driverLat && driverLng) {
    const celulas = geohashVizinhos(driverLat, driverLng); // agora 9 células
    unsubs = celulas.map(celula => {
      const q = query(
        collection(db, "rides"),
        where("status", "==", "pending"),
        where("regiao", "==", celula),
        orderBy("criadoEm", "desc"),
        limit(10)
      );
      return onSnapshot(q, (snap) => {
        snap.docs.forEach(d => todasCorridas.set(d.id, { id: d.id, ...d.data() }));
        snap.docChanges().forEach(change => {
          if (change.type === "removed") todasCorridas.delete(change.doc.id);
        });
        notificar();
      });
    });
  } else {
    // Fallback: sem localização — ouve tudo mas limita a 30
    const q = query(
      collection(db, "rides"),
      where("status", "==", "pending"),
      orderBy("criadoEm", "desc"),
      limit(30)
    );
    unsubs = [onSnapshot(q, (snap) => {
      snap.docChanges().forEach(change => {
        if (change.type === "removed") todasCorridas.delete(change.doc.id);
        else todasCorridas.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
      });
      notificar();
    })];
  }

  return () => {
    clearTimeout(debounceTimer);
    unsubs.forEach(u => u());
  };
}

// ─── ESCUTAR CORRIDAS DO PASSAGEIRO ─────────────────────────
// Limitado a 50 corridas recentes — para histórico completo use getHistoricoCorridas
export function escutarCorridasPassageiro(passageiroId, callback) {
  const q = query(
    collection(db, "rides"),
    where("passageiroId", "==", passageiroId),
    orderBy("criadoEm", "desc"),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ─── ESCUTAR CORRIDAS DO MOTORISTA ──────────────────────────
// Limitado a 50 corridas recentes — para histórico completo use getHistoricoCorridas
export function escutarCorridasMotorista(motoristaId, callback) {
  const q = query(
    collection(db, "rides"),
    where("motoristaid", "==", motoristaId),
    orderBy("criadoEm", "desc"),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ─── ESCUTAR CORRIDAS AGENDADAS (passageiro) ─────────────────
// Corridas agendadas visíveis para motoristas (status=scheduled, nos próximos 7 dias)
export function escutarAgendadasParaMotorista(cidade, callback) {
  const agora = new Date();
  const em7dias = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000);
  const q = query(
    collection(db, "scheduled_rides"),
    where("status", "==", "scheduled"),
    orderBy("agendadoPara", "asc"),
    limit(20)
  );
  return onSnapshot(q, (snap) => {
    const todas = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // filtra só as futuras e dos próximos 7 dias
    const filtradas = todas.filter(r => {
      const dt = r.agendadoPara ? new Date(r.agendadoPara) : null;
      return dt && dt >= agora && dt <= em7dias;
    });
    callback(filtradas);
  });
}

export function escutarCorridasAgendadas(passageiroId, callback) {
  const q = query(
    collection(db, "scheduled_rides"),
    where("passageiroId", "==", passageiroId),
    where("status", "in", ["scheduled", "pending"]),
    orderBy("agendadoPara", "asc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}
